import React, {memo, useEffect, useMemo, useState} from 'react';
import styled, {useTheme} from "styled-components";
import {ImageItem, ImageLane, ImageRune, ImageSpell} from "../common";
import {MarginBox} from "../ui/styled";
import InfluIcon from "../summoner/Profile/InfluInfo/InfluIcon";
import LLMChampionImage from "./llmChampionImage";
import useLLMMatches from "../../common/hooks/llm/useLLMMatches";
import {calcKda, renderGameTime} from "../../function";
import ItemWithMythic from "../summoner/ItemWIthMythic";
import useTimeHook from "../../common/hooks/useTimeHook";
import ToSummonerLink from "../Link/ToSummonerLink";
import ChampionImageWithLane from "../common/ChampionImageWithLane";
import StartItemImage from "../common/StartItemImage/StartItemImage";
import LlmRune from "./llmRune";
import LlmSkillSq from "./llmSkillSq";
import LlmItemSq from "./llmItemSq";
import {useTranslation} from "react-i18next";
import GradientScoreBox from "../common/GradientScoreBox/GradientScoreBox";

const Title = styled.div`
  font-weight: 600;
  font-size: 22px;
  color: ${props => props.theme.color.dark_100};

`;
const FiltersWrapper = styled.div`
  margin: 20px 0;
`;
const MatchWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;
const MoreButton = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 64px;
  background-color: ${props => props.theme.color.site_main_color};
  border: 1px solid ${props => props.theme.color.default_border_color};
  border-radius: 4px;

  &:hover {
    background-color: ${props => props.theme.color.white_200};
  }
`;

const ButtonSpan = styled.span`
  color: ${props => props.theme.color.dark_100};
  font-weight: 500;
  font-size: 16px;
  margin-right: 20px;
`;

const LlmMasterContent = ({pickChampion, againstChampion}) => {
    const {t} = useTranslation();
    const [page, setPage] = useState(1)
    const [laneFilter, setLaneFilter] = useState('all');
    const [masterFilter, setMasterFilter] = useState('ALL')
    const matches = useLLMMatches({page, againstChampion, pickChampion, masterFilter});
    const [matchList, setMatchList] = useState([]);
    const onChangeLaneFilter = (value) => {
        if (value !== laneFilter) {
            setLaneFilter(value)
        }
    }

    const onClickMoreButton = () => {
        setPage(prev => prev + 1)
    }
    const resetState = () => {
        setPage(1)
        setMatchList([]);
    }

    const onClickMasterFilter = (value) => {
        if (value !== masterFilter) {
            setMasterFilter(value)
            resetState()
        }
    }

    useEffect(() => {
        if (Array.isArray(matches.data) && matches.data.length > 0) {
            setMatchList(prev => [...prev, ...matches.data])
        }
    }, [matches.data])


    const filterMatch = useMemo(() => {
        try {
            if (laneFilter === 'all' && masterFilter === 'all') {
                return matchList
            }
            const filterLane = matchList.filter((data) => {
                if (laneFilter === 'all') {
                    return true
                }
                const replaceText = data.position.replace(/^.*_/, '');
                return replaceText.toLowerCase() === laneFilter.toLowerCase()
            });


            return filterLane
        } catch (e) {
            return []
        }
    }, [matchList, laneFilter, masterFilter])

    if (!pickChampion || !againstChampion) {
        return null
    }


    return (
        <div>
            <Title>{t('llm.highElo')}</Title>
            <FiltersWrapper>
                <Filters laneFilter={laneFilter} setLaneFilter={onChangeLaneFilter} masterFilter={masterFilter}
                         setMasterFilter={onClickMasterFilter}/>
            </FiltersWrapper>
            <MatchWrapper>
                <RenderMatch filterMatch={filterMatch}/>
                <RenderMoreButton onClick={onClickMoreButton} matches={matches} filteredList={filterMatch}/>
            </MatchWrapper>
        </div>
    );
};


const RenderMatch = ({filterMatch}) => {
    if (!Array.isArray(filterMatch)) {
        return null
    }

    if (filterMatch.length < 1) {
        return null
    }
    return filterMatch.map((data, index) => {
        return (
            <LLMMatches key={index} data={data}/>
        )
    })
}

const RenderMoreButton = ({filteredList, matches, onClick}) => {
    const {t} = useTranslation();
    const theme = useTheme();
    if (filteredList.length < 10) {
        return null
    }

    if (matches.data.length < 10) {
        return null
    }

    if (filteredList.length > 19) {
        return null
    }

    return (
        <MoreButton onClick={onClick}>
            <ButtonSpan>
                {t('summoner.moreChamp')}
            </ButtonSpan>
            <img src={`${theme.icon.path}/llm/llm_navigate_next.svg`} alt={''}/>
        </MoreButton>
    )
}

const FilterWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const LaneWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
`;


const Filters = ({laneFilter, masterFilter, setLaneFilter, setMasterFilter}) => {
    const theme = useTheme();
    const {t} = useTranslation();
    return (
        <FilterWrapper>
            <LaneWrapper>
                <FilterItem selected={laneFilter === 'all'} onClick={() => setLaneFilter('all')}>
                    {t('llm.all')}
                </FilterItem>
                <FilterItem selected={laneFilter === 'top'} onClick={() => setLaneFilter('top')}>
                    <MarginBox margin={'0 8px 0 0'}>
                        <ImageLane lane={'top'} color={'dark'}/>
                    </MarginBox>
                    <span>
                        {t('llm.top')}
                </span>
                </FilterItem>
                <FilterItem selected={laneFilter === 'jungle'} onClick={() => setLaneFilter('jungle')}>
                    <MarginBox margin={'0 8px 0 0'}>
                        <ImageLane lane={'jungle'} color={'dark'}/>
                    </MarginBox>
                    <span>
                        {t('llm.jungle')}
                </span>
                </FilterItem>
                <FilterItem selected={laneFilter === 'middle'} onClick={() => setLaneFilter('middle')}>
                    <MarginBox margin={'0 8px 0 0'}>
                        <ImageLane lane={'middle'} color={'dark'}/>
                    </MarginBox>
                    <span>
                        {t('llm.mid')}
                </span>
                </FilterItem>
                <FilterItem selected={laneFilter === 'bot'} onClick={() => setLaneFilter('bot')}>
                    <MarginBox margin={'0 8px 0 0'}>
                        <ImageLane lane={'bot'} color={'dark'}/>
                    </MarginBox>
                    <span>
                        {t('llm.bot')}
                </span>
                </FilterItem>
                <FilterItem selected={laneFilter === 'supporter'} onClick={() => setLaneFilter('supporter')}>
                    <MarginBox margin={'0 8px 0 0'}>
                        <ImageLane lane={'supporter'} color={'dark'}/>
                    </MarginBox>
                    <span>
                        {t('llm.support')}
                </span>
                </FilterItem>
            </LaneWrapper>
            <MarginBox margin={'0 16px'}>
                <img src={`${theme.icon.path}/llm/icon_llm_line.svg`} width={8} height={24} alt={''}/>
            </MarginBox>
            <LaneWrapper>
                <SmallFilterItem selected={masterFilter === 'ALL'} onClick={() => setMasterFilter('ALL')}>
                    {t('llm.all')}
                </SmallFilterItem>
                <SmallFilterItem selected={masterFilter === 'OTP'} onClick={() => setMasterFilter('OTP')}>
                    {t('llm.otp')}
                </SmallFilterItem>
                <SmallFilterItem selected={masterFilter === 'Pro'} onClick={() => setMasterFilter('Pro')}>
                    {t('llm.pro')}
                </SmallFilterItem>
            </LaneWrapper>
        </FilterWrapper>
    )
}
const ItemWrapper = styled.button`
  width: 64px;
  height: 32px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  background-color: ${props => {
    let color = 'transparent';
    if (props.selected) {
      color = props.theme.color.blue_600_01
    }
    return color;
  }};
  border: 1px solid ${props => {
    let color = 'transparent;'
    if (props.selected) {
      color = props.theme.color.green_100
    }
    return color
  }};
  font-weight: 700;
  font-size: 14px;
  color: ${props => props.theme.color.green_100};

  &:hover {
    background-color: ${props => {
      let color = props.theme.color.white_200;
      if (props.selected) {
        color = props.theme.color.blue_600_01
      }
      return color;
    }};
  }
`;


const SmallItem = styled.button`
  width: 56px;
  height: 32px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  background-color: ${props => {
    let color = 'transparent';
    if (props.selected) {
      color = props.theme.color.blue_600_01
    }
    return color;
  }};
  border: 1px solid ${props => {
    let color = 'transparent;'
    if (props.selected) {
      color = props.theme.color.green_100
    }
    return color
  }};
  font-weight: 700;
  font-size: 14px;
  color: ${props => props.theme.color.green_100};

  &:hover {
    background-color: ${props => {
      let color = props.theme.color.white_200;
      if (props.selected) {
        color = props.theme.color.blue_600_01
      }
      return color;
    }};
  }
`;

const FilterItem = ({children, selected, onClick}) => {
    return (
        <ItemWrapper selected={selected} onClick={onClick}>
            {children}
        </ItemWrapper>
    )
}
const SmallFilterItem = ({children, selected, onClick}) => {
    return (
        <SmallItem selected={selected} onClick={onClick}>
            {children}
        </SmallItem>
    )
}
const LLMMatchWrapper = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  max-height: 400px;
  display: flex;
  flex-direction: row;
  border-radius: 4px;
`;
const Bar = styled.div`
  position: absolute;
  background-color: ${props => {
    let color = '#EA6969'
    if (props.isBlue) {
      color = '#4C92FC'
    }
    return color
  }};
  height: 100%;
  width: 8px;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;

`;

const SummaryContent = styled.div`
  height: 72px;
  cursor: pointer;
  width: 1070px;
  margin-left: 8px;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 10px 0 10px 16px;
  border-top: 1px solid ${props => props.theme.color.default_border_color};
  border-right: 1px solid ${props => props.theme.color.default_border_color};
  border-bottom: 1px solid ${props => props.theme.color.default_border_color};
  border-top-right-radius: 4px;
  border-bottom-right-radius: ${props => {
    let radius = '4px';
    if (props.isOpen) {
      radius = 0;
    }
    return radius
  }};
  background-color: ${props => {
    let color = props.theme.color.red_400
    if (props.isBlue) {
      color = props.theme.color.blue_300
    }
    return color
  }};


  &:hover {
    background-color: ${props => {
      let color = props.theme.color.red_200
      if (props.isBlue) {
        color = props.theme.color.blue_200
      }
      return color
    }};
  }
;

`;
const ImageBox = styled.div`
  width: 24px;
  height: 24px;
  margin-right: 16px;

  .imgLane {
    width: 24px;
    height: 24px;
  }
`;

const SummonerImgWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  position: relative;
  width: 40px;
  height: 48px;
`;
const EmptySummoner = styled.div`
  width: 40px;
  height: 40px;
  background-color: ${props => props.theme.color.dark_300};
  border-radius: 100%;
`;
const SummonerAbsolute = styled.div`
  position: absolute;
  bottom: 0;
`;

const SummonerInfoWrapper = styled.div`
  width: 168px;
  display: flex;
  flex-direction: column;
  margin-left: 16px;

  a {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 19ch;
    width: fit-content;
  }
`;
const SummonerNameSpan = styled.span`
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  color: ${props => props.theme.color.dark_100};
  height: 17px;
  line-height: 17px;

  &:hover {
    text-decoration: underline;
  }
`;
const TierWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 18px;
`;

const TimeWrapper = styled.div`
  height: 14px;
  display: flex;
  flex-direction: row;
  align-items: center;
`;
const TierImageBox = styled.div`
  margin-right: 4px;
`;

const TierText = styled.span`
  font-weight: 400;
  font-size: 12px;
  color: ${props => props.theme.color.dark_200};
`;

const TimeText = styled.span`
  font-weight: 400;
  font-size: 12px;
  color: ${props => props.theme.color.dark_200};
`;

const GameTimeText = styled.span`
  font-weight: 400;
  font-size: 10px;
  color: ${props => props.theme.color.dark_200};
`;
const TimeBar = styled.div`
  width: 1px;
  height: 10px;
  background-color: ${props => props.theme.color.dark_200};
  margin: 0 4px;
`;
const ChampionImageWrapper = styled.div`
  margin-left: 16px;
  width: 132px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  .imgChampEmpty {
    width: 40px;
    height: 40px;
  }

  .imgDiv {
    border-radius: 100%;
  }

  .imgChamp {
    width: 46px;
    height: 46px;
  }
`;
const VersusText = styled.span`
  font-weight: 600;
  font-size: 12px;
  color: ${props => props.theme.color.dark_200};
`;
const InfoTitleText = styled.div`
  margin-bottom: 4px;
  color: ${props => props.theme.color.dark_200};
  font-weight: 600;
  font-size: 12px;
  line-height: 14px;
`;
const KDAText = styled.span`
  font-weight: 700;
  font-size: 16px;
  color: ${props => props.theme.color.dark_100};
  line-height: 19px;
`;
const KDAWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 88px;
  margin-left: 16px;
`;

const KDARatioText = styled.span`
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: ${props => props.theme.color.dark_200};
`;
const AIScoreWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 72px;
  height: 50px;
  margin-left: 8px;
  margin-right: 16px;
`;
const RuneBox = styled.div`
  width: 55px;
  height: 52px;
  position: relative;
`;
const BigRuneBox = styled.div`
  .imgRune {
    width: 40px;
    height: 40px;
    border: 1px solid #3C3C3C;
    border-radius: 100%;
    background-color: #262626;
  }
`;
const AbsoluteRuneBox = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;

  width: 30px;
  height: 30px;

  .imgRune {
    width: 28px;
    height: 28px;
    border: 1px solid #3C3C3C;
    border-radius: 100%;
    background-color: #262626;
  }
`;
const SpellWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  margin-left: 16px;

  .non_image {
    border-radius: 4px;
    overflow: hidden;
    width: 32px;
    height: 32px;
  }

  .imgSpell {
    border-radius: 4px;
    width: 32px;
    height: 32px;
  }
`;

const MatchItemWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 248px;
  margin-left: 16px;

  .imgItem {
    width: 32px;
    height: 32px;
    border-radius: 4px !important;
    margin-left: 4px;
  }
`;


const ContentBox = styled.div`
  width: 100%;
`;
const ProfileBannerImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 100%;
  object-fit: cover;
`;
const LLMMatches = memo(({data}) => {
    const [open, setOpen] = useState(false)
    const {lessMinWithIntl} = useTimeHook()
    const currentPosition = useMemo(() => {
        try {
            const replaceText = data.position.replace(/^.*_/, '');
            if (replaceText.toLowerCase() === 'supporter') {
                return 'Support'
            }
            return replaceText
        } catch (e) {
            return 'top'
        }
    }, [data.position]);

    const isBlue = useMemo(() => {
        return data.win
    }, [data.side])

    const getTierImageText = (value) => {
        if (value.toUpperCase() === 'MASTER') {
            return 'm'
        }

        if (value.toUpperCase() === 'GRANDMASTER') {
            return 'gm'
        }

        if (value.toUpperCase() === 'CHALLENGER') {
            return 'chall'
        }
        return 'm'
    }

    const userName = useMemo(() => {
        if (data.player_name) {
            return data.player_name
        }
        return `${data.riot_id_name}#${data.riot_id_tag_line}`
    }, [data]);

    const ProfileName = useMemo(() => {
        if (data.pro_streamer_status === 'pro') {
            return data.championship_name
        }
        return data.player_name
    }, [data])

    return (
        <LLMMatchWrapper isBlue={isBlue}>
            <Bar isBlue={isBlue} isOpen={open}/>
            <ContentBox>
                <SummaryContent isBlue={isBlue}
                                isOpen={open}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (open) {
                                        setOpen(false)
                                    } else {
                                        setOpen(true)
                                    }

                                }}>
                    <ImageBox>
                        <ImageLane color={'gray'} lane={currentPosition}/>
                    </ImageBox>
                    <SummonerImgWrapper>
                        {ProfileName ?
                            <ProfileBannerImage
                                src={`${process.env.REACT_APP_ROOT_IMG}${data.pro_streamer_status}/${ProfileName}.png`}
                                onError={(e) => e.target.src = '/images/img_unknown.svg'}/>
                            :
                            <ProfileBannerImage
                                src={'/images/img_unknown.svg'}
                            />

                        }
                        {/*<EmptySummoner/>*/}
                        <SummonerAbsolute>
                            <InfluIcon status={data.pro_streamer_status} width={34} height={14}/>
                        </SummonerAbsolute>
                    </SummonerImgWrapper>
                    <SummonerInfoWrapper>
                        <ToSummonerLink puu_id={data.puu_id} name={`${data.riot_id_name}-${data.riot_id_tag_line}`}
                                        region={'KR'}>
                            <SummonerNameSpan>
                                {userName}
                            </SummonerNameSpan>
                        </ToSummonerLink>
                        <TierWrapper>
                            <TierImageBox>
                                <img src={`/images/llm/icn_${getTierImageText(data.tier)}.svg`} width={18} height={18}
                                     alt={''}/>
                            </TierImageBox>
                            <TierText>{data.tier}</TierText>
                        </TierWrapper>
                        <TimeWrapper>
                            <TimeText>{lessMinWithIntl(data.game_end_time * 1000)}</TimeText>
                            <TimeBar/>
                            <GameTimeText>{renderGameTime(data.game_duration)}</GameTimeText>
                        </TimeWrapper>
                    </SummonerInfoWrapper>
                    <ChampionImageWrapper>
                        <LLMChampionImage championId={data.champion_id} isWin={isBlue} disableChampion={true}/>
                        <VersusText>VS</VersusText>
                        <ChampionImageWithLane champion_id={data.enemy_champion_id} imgSize={100} disabled={true}/>
                    </ChampionImageWrapper>
                    <KDAWrapper>
                        <MarginBox margin={'0 0 4px 0'}>
                            <KDAText>{data.kills} / {data.deaths} / {data.assists}</KDAText>
                        </MarginBox>
                        <KDARatioText>{calcKda({
                            deaths: data.deaths,
                            kills: data.kills,
                            assists: data.assists
                        })} KDA</KDARatioText>
                    </KDAWrapper>
                    <AIScoreWrapper>
                        <InfoTitleText>AI-Score</InfoTitleText>
                        <GradientScoreBox>
                            {data?.ai_score?.toFixed(0)}
                        </GradientScoreBox>
                    </AIScoreWrapper>
                    <RuneBox>
                        <BigRuneBox>
                            <ImageRune runeId={data.rune.perk_0}/>
                        </BigRuneBox>
                        <AbsoluteRuneBox>
                            <ImageRune runeId={data.rune.perk_sub_style}/>
                        </AbsoluteRuneBox>
                    </RuneBox>
                    <SpellWrapper>
                        <ImageSpell width={32} height={32} spellId={data.spell.spell_1} smite={data.smite}/>
                        <ImageSpell width={32} height={32} spellId={data.spell.spell_2} smite={data.smite}/>
                    </SpellWrapper>
                    <MatchItemWrapper>
                        <ItemWithMythic item={data.item_final} width={20}
                                        array={[data.item_final[0], data.item_final[1], data.item_final[2], data.item_final[3], data.item_final[4], data.item_final[5]]}
                                        lens={data.item_final[6]} isWin={isBlue}/>
                    </MatchItemWrapper>
                </SummaryContent>
                {open ? <OpenContent data={data} isWin={isBlue}/> : null}
            </ContentBox>
        </LLMMatchWrapper>

    )
})
const OpenContentWrapper = styled.div`
  margin-left: 8px;
  width: 1072px;
  display: flex;
  flex-direction: row;
  padding: 20px 16px;
  min-height: 240px;
  background-color: ${props => {
    let color = props.theme.color.red_400_50
    if (props.isBlue) {
      color = props.theme.color.blue_300_50
    }
    return color
  }};
`;

const OpenContentContentBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  & + & {
    margin-left: 12px;
  }

  .imgSpell {
    border-radius: 4px;
  }

  .imgItem {
    width: 32px;
    height: 32px;
    border-radius: 4px;
  }
;
`;

const OpenContentItem = styled.div`
  height: 200px;
  display: flex;
  flex-direction: row;

  .imgSkill {
    border-radius: 4px;
  }
`;


const WithoutContentItem = styled.div`

`;


const OpenContentPaddingItem = styled(OpenContentItem)`
  padding: 8px;
`;

const OpenContentTeamTitle = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  width: 100%;
  font-weight: 600;
  font-size: 10px;
  line-height: 12px;
  color: ${props => props.theme.color.dark_100};
  margin-bottom: 8px;
`;

const LineBox = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  height: 176px;
`;


const OpenSpellBox = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;

  .imgSpell {
    border-radius: 4px;
    width: 32px;
    height: 32px;
  }
`;
const SpellImageBox = styled.div`
  display: flex;
  flex-direction: column;

  & + & {
    margin-top: 22px;
  }
`;
const OpenLineBar = styled.div`
  min-width: 1px;
  width: 1px;
  height: 200px;
  background-color: ${props => props.theme.color.dark_300};
  margin: 0 13px;
`;
const SeqBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 36px;
`;
const OpenContent = ({data, isWin}) => {
    const {t} = useTranslation();
    const skillList = useMemo(() => {
        try {
            return data.skill.map((item => {
                return item.skill_id
            }))
        } catch (e) {
            return []
        }
    }, [data.skill]);

    const redTeam = useMemo(() => {

        try {
            const positionData = data.positions_datas
            return [positionData.RED_Top, positionData.RED_Jungle, positionData.RED_Middle, positionData.RED_Bot, positionData.RED_Supporter]
        } catch (e) {
            return [];
        }
    }, [data.positions_datas])

    const blueTeam = useMemo(() => {

        try {
            const positionData = data.positions_datas
            return [positionData.BLUE_Top, positionData.BLUE_Jungle, positionData.BLUE_Middle, positionData.BLUE_Bot, positionData.BLUE_Supporter]
        } catch (e) {
            return [];
        }
    }, [data.positions_datas]);


    const myTeam = useMemo(() => {
        try {
            const isRedTeam = redTeam.some((champ) => champ === data.champion_id)
            if (isRedTeam) {
                return redTeam
            } else {
                return blueTeam
            }
        } catch (e) {
            return [];
        }
    }, []);


    const enemyTeam = useMemo(() => {
        try {
            const isRedTeam = redTeam.some((champ) => champ === data.champion_id)
            if (isRedTeam) {
                return blueTeam
            } else {
                return redTeam
            }
        } catch (e) {
            return [];
        }
    }, []);

    return (
        <OpenContentWrapper isBlue={isWin}>
            <OpenContentItem>
                <OpenContentContentBox>
                    <OpenContentTeamTitle>{t('llm.team')}</OpenContentTeamTitle>
                    <LineBox>
                        <LaneLine/>
                        <OpenContentTeamColorLine isBlue={data.win} team={myTeam} champion_id={data.champion_id}/>
                        <OpenContentTeamColorLine team={enemyTeam} champion_id={data.champion_id} isBlue={!data.win}/>
                    </LineBox>
                </OpenContentContentBox>

                <OpenContentContentBox>
                    <SpellImageBox>
                        <OpenContentTeamTitle>{t('llm.spell')}</OpenContentTeamTitle>
                        <OpenSpellBox>
                            <ImageSpell width={32} height={32} spellId={data.spell.spell_1} smite={data.smite}/>
                            <ImageSpell width={32} height={32} spellId={data.spell.spell_2} smite={data.smite}/>
                        </OpenSpellBox>
                    </SpellImageBox>
                    <SpellImageBox>
                        <OpenContentTeamTitle>{t('llm.starter')}</OpenContentTeamTitle>
                        <OpenSpellBox>
                            <StartItemImage list={data?.item_start?.slice(0, 2)}/>
                        </OpenSpellBox>
                    </SpellImageBox>
                    <SpellImageBox>
                        <OpenContentTeamTitle>{t('llm.core')}</OpenContentTeamTitle>
                        <OpenSpellBox>
                            {data.item_core.map((item, index) => {
                                return <ImageItem width={16} height={16} itemId={item} key={index}/>
                            })}

                        </OpenSpellBox>
                    </SpellImageBox>
                </OpenContentContentBox>
            </OpenContentItem>
            <OpenLineBar/>
            <OpenContentPaddingItem>
                <LlmRune runeList={{
                    rune_main: [data.rune.perk_primary_style, data.rune.perk_0, data.rune.perk_1, data.rune.perk_2, data.rune.perk_3],
                    rune_sub: [data.rune.perk_sub_style, data.rune.perk_4, data.rune.perk_5],
                    rune_stat: [data.rune?.stat_perk_2, data.rune?.stat_perk_1, data.rune?.stat_perk_0],
                }}/>
            </OpenContentPaddingItem>
            <OpenLineBar/>
            <MarginBox margin={'4px 0 0 0'}>
                <LlmSkillSq champion_id={data.champion_id} list={skillList}/>
                <SeqBox>
                    <LlmItemSq list={data.item_seq}/>
                </SeqBox>
            </MarginBox>
        </OpenContentWrapper>
    )
}
// let rune_main = [runeData?.perk_primary_style, runeData?.perk_0, runeData?.perk_1, runeData?.perk_2, runeData?.perk_3];
// let rune_sub = [runeData?.perk_sub_style, runeData?.perk_4, runeData?.perk_5];
// let rune_stat = [runeData?.stat_perk_0, runeData?.stat_perk_1, runeData?.stat_perk_2]

const ContentTeamColorWrapper = styled.div`
  padding: 4px;
  gap: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  background-color: ${props => {
    let color = '#E85C5C'
    if (props.isWin) {
      color = '#4C92FC';
    }
    return color
  }};
  border-radius: 4px;


  .imgChamp {
    width: 26px;

    height: 26px;
`;

const LaneLineWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 4px 0;

  .imgLane {
    width: 16px;
    height: 24px;
  }
`;
const ChampImageWrapper = styled.div`
  .imgDiv {
    width: 24px;
    height: 24px;
    border-radius: 4px;
    border: 2px solid ${props => {
      let color = 'transparent'
      if (props.isSelected) {
        color = '#fff'
      }
      return color
    }};
  }
`;
const LaneLine = () => {
    return (
        <LaneLineWrapper>
            <ImageLane lane={'top'} color={'gray'}/>
            <ImageLane lane={'jungle'} color={'gray'}/>
            <ImageLane lane={'middle'} color={'gray'}/>
            <ImageLane lane={'bot'} color={'gray'}/>
            <ImageLane lane={'support'} color={'gray'}/>
        </LaneLineWrapper>
    )
}
const OpenContentTeamColorLine = ({isBlue, team, champion_id}) => {
    return (
        <ContentTeamColorWrapper isWin={isBlue}>
            {team.map((item) => {
                return (
                    <ChampImageWrapper key={item} isSelected={champion_id === item}>
                        <ChampionImageWithLane champion_id={item} disableScale={true}/>
                    </ChampImageWrapper>
                )
            })}
        </ContentTeamColorWrapper>
    )
}


export default LlmMasterContent;